import { useTranslation } from "react-i18next";
import { Button } from "~/atoms/button";
import { ControlledCombobox } from "~/molecules/ControlledCombobox";
import { DocumentBreadcrumbs } from "~/molecules/DocumentBreadcrumbs";
import HeadingPage from "~/molecules/HeadingPage";
import { type Option } from "~/molecules/ControlledCombobox";
import { type WorkflowDetails } from "~/api/types";

interface WorkflowPageHeaderProps {
  workflow: WorkflowDetails;
  isPreviewMode: boolean;
  isApplying: boolean;
  hasUnsavedChanges: boolean;
  showTemplateDropdown: boolean;
  selectedTemplateId: string | null;
  templateOptions: Option[];
  onCancelPreview: () => void;
  onApplyTemplate: () => void;
  onRequestApplyConfirm: () => void;
  onTemplateSelect: (value: string) => void;
}

export function WorkflowPageHeader({
  workflow,
  isPreviewMode,
  isApplying,
  hasUnsavedChanges,
  showTemplateDropdown,
  selectedTemplateId,
  templateOptions,
  onCancelPreview,
  onApplyTemplate,
  onRequestApplyConfirm,
  onTemplateSelect,
}: WorkflowPageHeaderProps) {
  const { t } = useTranslation();
  const heading = t(($) => $.commons.workflows.edit);

  return (
    <>
      <DocumentBreadcrumbs
        documentId={workflow.document.id}
        versionId={workflow.documentVersion.id}
        title={workflow.documentVersion.title}
        additionalBreadcrumbs={[{ label: heading }]}
      />

      <div className="flex items-center justify-between">
        <HeadingPage title={heading} />

        <div className="flex items-center gap-2">
          {isPreviewMode && (
            <>
              <Button
                variant="outline"
                onClick={onCancelPreview}
                disabled={isApplying}
              >
                {t(($) => $.commons.buttons.cancel)}
              </Button>
              <Button
                onClick={
                  hasUnsavedChanges ? onRequestApplyConfirm : onApplyTemplate
                }
                disabled={isApplying}
              >
                {isApplying
                  ? t(($) => $.workflowTemplates.applying)
                  : t(($) => $.workflowTemplates.applyOnWorkflow.apply)}
              </Button>
            </>
          )}

          {showTemplateDropdown && (
            <div className="w-55">
              <ControlledCombobox
                value={selectedTemplateId}
                options={templateOptions}
                placeholder={t(
                  ($) => $.workflowTemplates.applyOnWorkflow.selectPlaceholder,
                )}
                showSearch={templateOptions.length > 5}
                onChange={onTemplateSelect}
                disabled={isApplying}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
